import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to get Clerk token (will be set by App component)
let getClerkToken: (() => Promise<string | null>) | null = null;

export const setClerkTokenGetter = (getter: () => Promise<string | null>) => {
  getClerkToken = getter;
};

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  if (getClerkToken) {
    try {
      const token = await getClerkToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('[API] No Clerk token available for request:', config.url);
      }
    } catch (error) {
      console.error('[API] Error getting Clerk token:', error);
    }
  } else {
    console.warn('[API] Clerk token getter not set for request:', config.url);
  }
  return config;
});

// Integrations API
export const integrationsApi = {
  // Save Shopify credentials
  saveShopify: async (shopUrl: string, accessToken: string, apiSecret: string) => {
    const response = await api.post('/api/integrations/shopify', {
      shop_url: shopUrl,
      access_token: accessToken,
      api_secret: apiSecret,
    });
    return response.data;
  },

  // Save UPS account info
  saveUps: async (upsData: {
    account_number: string;
    account_type: string;
    postal_code: string;
    country_code: string;
    negotiated_rates: boolean;
  }) => {
    const response = await api.post('/api/integrations/ups', upsData);
    return response.data;
  },

  // Save USPS credentials
  saveUsps: async (consumerKey: string, consumerSecret: string) => {
    const response = await api.post('/api/integrations/usps', {
      consumer_key: consumerKey,
      consumer_secret: consumerSecret,
    });
    return response.data;
  },

  // Save BarTender config
  saveBartender: async (apiUrl: string) => {
    const response = await api.post('/api/integrations/bartender', {
      api_url: apiUrl,
    });
    return response.data;
  },

  // Get integration status
  getStatus: async () => {
    const response = await api.get('/api/integrations/status');
    return response.data;
  },

  // Test connection
  testConnection: async (provider: string) => {
    const response = await api.post(`/api/integrations/${provider}/test`);
    return response.data;
  },
};

// Products API
export const productsApi = {
  // List products
  list: async (params?: { limit?: number; offset?: number; status?: string }) => {
    const response = await api.get('/api/products', { params });
    return response.data;
  },

  // Get single product
  get: async (id: string) => {
    const response = await api.get(`/api/products/${id}`);
    return response.data;
  },

  // Refresh a variant's weight from Shopify
  refreshVariantWeight: async (variantId: string) => {
    const response = await api.post(`/api/products/variants/${variantId}/refresh-weight`);
    return response.data;
  },

  // Search products
  search: async (query: string) => {
    const response = await api.get('/api/products/search', { params: { q: query } });
    return response.data;
  },

  // List all variants with product info (for bundle dropdown)
  listAllVariants: async () => {
    const response = await api.get('/api/products/variants');
    return response.data;
  },

  // Sync products from Shopify
  sync: async () => {
    const response = await api.post('/api/products/sync');
    return response.data;
  },

  // Update packing verification requirement
  updatePackingVerification: async (productId: string, required: boolean) => {
    const response = await api.patch(`/api/products/${productId}/packing-verification`, {
      requires_packing_verification: required,
    });
    return response.data;
  },

  // Update variant-level packing verification (null = inherit, true/false = override)
  updateVariantPackingVerification: async (variantId: string, required: boolean | null) => {
    const response = await api.patch(`/api/products/variants/${variantId}/packing-verification`, {
      requires_packing_verification: required,
    });
    return response.data;
  },

  // Update product details (weight, barcode, hs_tariff_code, country_of_origin, etc.)
  update: async (productId: string, data: { 
    weight?: number; 
    barcode?: string;
    hs_tariff_code?: string;
    country_of_origin?: string;
  }) => {
    const response = await api.patch(`/api/products/${productId}`, data);
    return response.data;
  },

  // Get products missing HS codes
  getMissingHSCodes: async () => {
    const response = await api.get('/api/products/missing-hs-codes');
    return response.data;
  },

  // Bulk update HS codes
  bulkUpdateHSCodes: async (updates: Array<{ product_id: string; hs_tariff_code: string; country_of_origin?: string }>) => {
    const response = await api.post('/api/products/bulk-hs-codes', { updates });
    return response.data;
  },
};

// Orders API
export const ordersApi = {
  // List orders
  list: async (params?: {
    limit?: number;
    offset?: number;
    status?: string;
    sort_by?: string;
    sort_dir?: string;
    search?: string;
  }) => {
    const response = await api.get('/api/orders', { params });
    return response.data;
  },

  // Fast lookup by order number
  lookup: async (orderNumber: string) => {
    const response = await api.get('/api/orders/lookup', { params: { order_number: orderNumber } });
    return response.data;
  },

  // Get single order
  get: async (id: string) => {
    const response = await api.get(`/api/orders/${id}`);
    return response.data;
  },

  // Update order status
  updateStatus: async (id: string, status: string) => {
    const response = await api.patch(`/api/orders/${id}/status`, { status });
    return response.data;
  },

  // Sync orders from Shopify
  sync: async () => {
    const response = await api.post('/api/orders/sync');
    return response.data;
  },

  // Update existing orders with product data
  updateProductData: async () => {
    const response = await api.patch('/api/orders/update-product-data');
    return response.data;
  },

  // Mark pickup order as ready
  markPickupReady: async (id: string) => {
    const response = await api.patch(`/api/orders/${id}/pickup-ready`);
    return response.data;
  },

  // Mark pickup order as picked up (completed)
  markPickedUp: async (id: string) => {
    const response = await api.patch(`/api/orders/${id}/picked-up`);
    return response.data;
  },

  // Alias for markPickedUp
  markPickupCompleted: async (id: string) => {
    const response = await api.patch(`/api/orders/${id}/picked-up`);
    return response.data;
  },

  // Send pickup reminder email
  sendPickupReminder: async (id: string) => {
    const response = await api.post(`/api/orders/${id}/pickup-reminder`);
    return response.data;
  },

  // Update shipping address
  updateAddress: async (id: string, address: {
    address_line1: string;
    address_line2?: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
  }) => {
    const response = await api.patch(`/api/orders/${id}/address`, address);
    return response.data;
  },

  // Create a manual order
  create: async (data: {
    customer: {
      id?: string;
      first_name: string;
      last_name: string;
      email?: string;
      phone?: string;
    };
    items: Array<{
      product_id?: string;
      product_variant_id?: string;
      title: string;
      sku: string;
      quantity: number;
      price: number;
      weight_oz: number;
      hs_tariff_code?: string;
      country_of_origin?: string;
    }>;
    shipping_address?: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      zip: string;
      country?: string;
    };
    order_type: 'shipping' | 'pickup';
    notes?: string;
    payment_terms?: string;
  }) => {
    const response = await api.post('/api/orders', data);
    return response.data;
  },

  // Re-sync a single order from Shopify to refresh shipping_lines and other data
  resync: async (id: string) => {
    const response = await api.post(`/api/orders/${id}/resync`);
    return response.data;
  },
};

// Scans API
export const scansApi = {
  // Record item scan
  recordScan: async (orderId: string, orderItemId: string, department: string) => {
    const response = await api.post('/api/scans', {
      order_id: orderId,
      order_item_id: orderItemId,
      department,
    });
    return response.data;
  },

  // Get scans for an order
  getOrderScans: async (orderId: string) => {
    const response = await api.get(`/api/scans/${orderId}`);
    return response.data;
  },

  // Clear scans
  clearScans: async (orderId: string) => {
    const response = await api.delete(`/api/scans/${orderId}`);
    return response.data;
  },
};

// Stats API
export const statsApi = {
  // Get dashboard stats
  getStats: async () => {
    const response = await api.get('/api/stats');
    return response.data;
  },
  // Get SLA breached orders (uses proper SLA settings including business hours)
  getSlaBreachedOrders: async () => {
    const response = await api.get('/api/sla/breaches');
    return response.data;
  },
};

// Shipping API
export const shippingApi = {
  // List all shipments for the organization
  listShipments: async (params?: {
    status?: string;
    date_from?: string;
    date_to?: string;
    carrier?: string;
    limit?: number;
    offset?: number;
  }) => {
    const response = await api.get('/api/shipping/shipments', { params });
    return response.data;
  },

  // Get shipments for an order (with labels)
  getShipments: async (orderId: string) => {
    const response = await api.get(`/api/shipping/shipments/${orderId}`);
    return response.data;
  },

  // Void/cancel a shipment
  voidShipment: async (shipmentId: string) => {
    const response = await api.delete(`/api/shipping/shipments/${shipmentId}`);
    return response.data;
  },

  // Create a return label from an existing outbound shipment
  createReturnLabel: async (shipmentId: string, options?: {
    labelFormat?: 'pdf' | 'png' | 'zpl';
    items?: Array<{ order_item_id: string; quantity: number }>;
    weight?: number;
    length?: number;
    width?: number;
    height?: number;
  }) => {
    const response = await api.post('/api/shipping/return-label', {
      shipment_id: shipmentId,
      label_format: options?.labelFormat || 'pdf',
      items: options?.items,
      weight: options?.weight,
      length: options?.length,
      width: options?.width,
      height: options?.height,
    });
    return response.data;
  },

  // Get shipping rates for an order
  // insurance_amount: optional, if over $100 rates will include insurance cost
  // insurance_provider: 'none', 'carrier' (UPS Declared Value), or 'parcelguard' (cheaper 3rd party)
  // signature_option: 'none', 'signature', or 'adult_signature'
  getRates: async (orderId: string, boxes: any[], insuranceAmount?: number, insuranceProvider?: 'none' | 'carrier' | 'parcelguard', signatureOption?: 'none' | 'signature' | 'adult_signature') => {
    const response = await api.post('/api/shipping/rates', {
      order_id: orderId,
      boxes,
      insurance_amount: insuranceAmount,
      insurance_provider: insuranceProvider || 'none',
      signature_option: signatureOption || 'none',
    });
    return response.data;
  },

  estimateRates: async (params: {
    to_zip: string;
    to_state?: string;
    to_city?: string;
    to_address_line1?: string;
    to_address_line2?: string;
    to_country?: string;
    box_selections?: Array<{ preset_id: string; quantity: number; weight_oz?: number }>;
    custom_boxes?: Array<{
      length: number;
      width: number;
      height: number;
      weight_oz: number;
      quantity: number;
    }>;
  }) => {
    const response = await api.post('/api/shipping/rates-estimate', params, { timeout: 90000 });
    return response.data;
  },

  lookupZip: async (zip: string) => {
    const response = await api.get('/api/shipping/zip-lookup', { params: { zip } });
    return response.data;
  },

  // Create shipping labels
  // label_format: 'pdf' (default), 'png', or 'zpl' (for thermal printers)
  // signature_required: 'none', 'signature', or 'adult_signature'
  // insurance_provider: 'none', 'carrier', or 'parcelguard'
  createLabels: async (
    orderId: string, 
    boxes: any[], 
    selectedRate: any, 
    options?: {
      labelFormat?: 'pdf' | 'png' | 'zpl';
      insuranceAmount?: number;
      insuranceProvider?: 'none' | 'carrier' | 'parcelguard';
      signatureRequired?: 'none' | 'signature' | 'adult_signature';
      reference1?: string;
      reference2?: string;
      skipFulfillment?: boolean;
      employeeId?: string;
    }
  ) => {
    const response = await api.post('/api/shipping/labels', {
      order_id: orderId,
      boxes,
      selected_rate: selectedRate,
      label_format: options?.labelFormat || 'pdf',
      insurance_amount: options?.insuranceAmount || 0,
      insurance_provider: options?.insuranceProvider || 'none',
      signature_required: options?.signatureRequired || 'none',
      reference1: options?.reference1 || '',
      reference2: options?.reference2 || '',
      skip_fulfillment: options?.skipFulfillment || false,
      employee_id: options?.employeeId,
    });
    return response.data;
  },

  // Batch create shipping labels for multiple orders
  batchCreateLabels: async (orderIds: string[], autoSelectCheapest: boolean = true) => {
    const response = await api.post('/api/shipping/batch-labels', {
      order_ids: orderIds,
      auto_select_cheapest: autoSelectCheapest,
    });
    return response.data;
  },

  // Confirm shipment and send to Shopify
  confirmShipment: async (shipmentId: string, skipFulfillment: boolean = false, employeeId?: string) => {
    const response = await api.post('/api/shipping/confirm', {
      shipment_id: shipmentId,
      skip_fulfillment: skipFulfillment,
      employee_id: employeeId,
    });
    return response.data;
  },

  // Create a manual shipment (no ShipEngine label)
  createManualShipment: async (
    orderId: string,
    payload: {
      carrier: string;
      tracking_number: string;
      cost?: number;
      service_level?: string;
      skip_fulfillment?: boolean;
      packages?: Array<{
        tracking_number?: string;
        weight?: number;
        length?: number;
        width?: number;
        height?: number;
        items?: Array<{
          order_item_id: string;
          shopify_line_item_id?: string;
          quantity: number;
        }>;
      }>;
    }
  ) => {
    const response = await api.post('/api/shipping/manual-shipment', {
      order_id: orderId,
      ...payload,
    });
    return response.data;
  },

  // Emergency void by tracking number (for orphaned labels)
  voidByTrackingNumber: async (trackingNumber: string, carrier: 'ups' | 'usps') => {
    const response = await api.post('/api/shipping/void-by-tracking', {
      tracking_number: trackingNumber,
      carrier,
    });
    return response.data;
  },

  // Package Presets
  getPackagePresets: async () => {
    const response = await api.get('/api/shipping/package-presets');
    return response.data;
  },

  savePackagePresets: async (presets: Array<{
    id?: string;
    name: string;
    length: number;
    width: number;
    height: number;
    weight?: number;
    is_default?: boolean;
  }>) => {
    const response = await api.put('/api/shipping/package-presets', { presets });
    return response.data;
  },

  addPackagePreset: async (preset: {
    name: string;
    length: number;
    width: number;
    height: number;
    weight?: number;
    is_default?: boolean;
  }) => {
    const response = await api.post('/api/shipping/package-presets', preset);
    return response.data;
  },

  deletePackagePreset: async (presetId: string) => {
    const response = await api.delete(`/api/shipping/package-presets/${presetId}`);
    return response.data;
  },

  // USPS SCAN Form / Manifest
  // Pass either { date } for a single day or { startDate, endDate } for a range.
  // Omit all to default to today.
  createManifest: async (params?: { date?: string; startDate?: string; endDate?: string }) => {
    const response = await api.post('/api/shipping/manifest', params || {});
    return response.data;
  },

  getManifests: async (limit?: number) => {
    const response = await api.get('/api/shipping/manifests', { params: { limit } });
    return response.data;
  },
};

// Employees API
export const employeesApi = {
  // List all employees
  list: async () => {
    const response = await api.get('/api/employees');
    return response.data;
  },

  // Get single employee
  get: async (id: string) => {
    const response = await api.get(`/api/employees/${id}`);
    return response.data;
  },

  // Create employee
  create: async (employeeData: {
    first_name: string;
    last_name: string;
    pin: string;
    employee_number?: string;
    role?: string;
    permissions?: string[];
  }) => {
    const response = await api.post('/api/employees', employeeData);
    return response.data;
  },

  // Update employee
  update: async (id: string, employeeData: any) => {
    const response = await api.patch(`/api/employees/${id}`, employeeData);
    return response.data;
  },

  // Delete employee
  delete: async (id: string) => {
    const response = await api.delete(`/api/employees/${id}`);
    return response.data;
  },

  // Authenticate with PIN
  authenticate: async (pin: string) => {
    const response = await api.post('/api/employees/auth', { pin });
    return response.data;
  },

  // Log activity
  logActivity: async (activityData: {
    employee_id: string;
    order_id: string;
    action_type: string;
    description?: string;
    item_id?: string;
    quantity?: number;
    metadata?: any;
  }) => {
    const response = await api.post('/api/employees/log-activity', activityData);
    return response.data;
  },

  // Get order activity
  getOrderActivity: async (orderId: string) => {
    const response = await api.get(`/api/employees/activity/${orderId}`);
    return response.data;
  },

  // Clock in
  clockIn: async (employee_id: string) => {
    const response = await api.post('/api/employees/clock-in', { employee_id });
    return response.data;
  },

  // Clock out
  clockOut: async (employee_id: string, notes?: string) => {
    const response = await api.post('/api/employees/clock-out', { employee_id, notes });
    return response.data;
  },

  // Get time status
  getTimeStatus: async (employee_id: string) => {
    const response = await api.get(`/api/employees/${employee_id}/time-status`);
    return response.data;
  },

  // Get time entries
  getTimeEntries: async (employee_id: string, start_date?: string, end_date?: string) => {
    const params = new URLSearchParams();
    if (start_date) params.append('start_date', start_date);
    if (end_date) params.append('end_date', end_date);
    const response = await api.get(`/api/employees/${employee_id}/time-entries?${params.toString()}`);
    return response.data;
  },

  // Get time report for all employees
  getTimeReport: async (start_date?: string, end_date?: string) => {
    const params = new URLSearchParams();
    if (start_date) params.append('start_date', start_date);
    if (end_date) params.append('end_date', end_date);
    const response = await api.get(`/api/employees/time-report?${params.toString()}`);
    return response.data;
  },

  // Get currently clocked-in employees
  getCurrentlyWorking: async () => {
    const response = await api.get('/api/employees/currently-working');
    return response.data;
  },

  // Get all time entries (with optional filters)
  getAllTimeEntries: async (params?: { employee_id?: string; start_date?: string; end_date?: string; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.employee_id) searchParams.append('employee_id', params.employee_id);
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    const response = await api.get(`/api/employees/time-entries?${searchParams.toString()}`);
    return response.data;
  },

  // Create manual time entry
  createTimeEntry: async (data: { employee_id: string; clock_in: string; clock_out?: string; notes?: string }) => {
    const response = await api.post('/api/employees/time-entries', data);
    return response.data;
  },

  // Update time entry
  updateTimeEntry: async (id: string, data: { clock_in?: string; clock_out?: string | null; notes?: string }) => {
    const response = await api.patch(`/api/employees/time-entries/${id}`, data);
    return response.data;
  },

  // Delete time entry
  deleteTimeEntry: async (id: string) => {
    const response = await api.delete(`/api/employees/time-entries/${id}`);
    return response.data;
  },
};

// Automation Rules API
export const automationRulesApi = {
  // List all automation rules
  list: async () => {
    const response = await api.get('/api/automation-rules');
    return response.data;
  },

  // Get single automation rule
  get: async (id: string) => {
    const response = await api.get(`/api/automation-rules/${id}`);
    return response.data;
  },

  // Create automation rule
  create: async (ruleData: {
    name: string;
    description?: string;
    priority?: number;
    enabled?: boolean;
    conditions: any;
    actions: any;
  }) => {
    const response = await api.post('/api/automation-rules', ruleData);
    return response.data;
  },

  // Update automation rule
  update: async (id: string, ruleData: any) => {
    const response = await api.patch(`/api/automation-rules/${id}`, ruleData);
    return response.data;
  },

  // Delete automation rule
  delete: async (id: string) => {
    const response = await api.delete(`/api/automation-rules/${id}`);
    return response.data;
  },

  // Test rule against an order
  test: async (orderId: string, conditions: any) => {
    const response = await api.post('/api/automation-rules/test', {
      order_id: orderId,
      conditions,
    });
    return response.data;
  },

  // Get rule templates
  getTemplates: async () => {
    const response = await api.get('/api/automation-rules/templates');
    return response.data;
  },

  // Evaluate rules for an order (returns applied settings)
  evaluateForOrder: async (orderId: string) => {
    const response = await api.get(`/api/automation-rules/evaluate/${orderId}`);
    return response.data;
  },

  // Reapply automation rules to existing orders
  reapplyToOrders: async (orderIds?: string[]) => {
    const response = await api.post('/api/automation-rules/reapply', { orderIds });
    return response.data;
  },
};

// Locations API
export const locationsApi = {
  // List all locations
  list: async () => {
    const response = await api.get('/api/locations');
    return response.data;
  },

  // Get default location
  getDefault: async () => {
    const response = await api.get('/api/locations/default');
    return response.data;
  },

  // Create location
  create: async (locationData: {
    name: string;
    company_name: string;
    contact_name?: string;
    phone?: string;
    email?: string;
    address_line1: string;
    address_line2?: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
    is_default?: boolean;
  }) => {
    const response = await api.post('/api/locations', locationData);
    return response.data;
  },

  // Update location
  update: async (id: string, locationData: any) => {
    const response = await api.patch(`/api/locations/${id}`, locationData);
    return response.data;
  },

  // Set as default
  setDefault: async (id: string) => {
    const response = await api.patch(`/api/locations/${id}/set-default`);
    return response.data;
  },

  // Delete location
  delete: async (id: string) => {
    const response = await api.delete(`/api/locations/${id}`);
    return response.data;
  },
};

// Wallet API
export const walletApi = {
  // Get wallet balance
  getBalance: async () => {
    const response = await api.get('/api/wallet/balance');
    return response.data;
  },

  // Get transaction history
  getTransactions: async (params?: { limit?: number; offset?: number }) => {
    const response = await api.get('/api/wallet/transactions', { params });
    return response.data;
  },

  // Add funds (after Square payment)
  addFunds: async (amount: number, sourceId: string) => {
    const response = await api.post('/api/wallet/add-funds', {
      amount,
      source_id: sourceId,
    });
    return response.data;
  },
};

// SLA API
export const slaApi = {
  // Get SLA settings
  getSettings: async () => {
    const response = await api.get('/api/sla/settings');
    return response.data;
  },

  // Update SLA settings
  updateSettings: async (settings: {
    enabled: boolean;
    shipping_threshold_hours: number;
    pickup_threshold_hours: number;
    use_business_hours?: boolean;
    business_days?: number[];
    business_start_time?: string;
    business_end_time?: string;
    timezone?: string;
  }) => {
    const response = await api.patch('/api/sla/settings', settings);
    return response.data;
  },

  // Get breached orders
  getBreachedOrders: async () => {
    const response = await api.get('/api/sla/breaches');
    return response.data;
  },

  // Trigger manual SLA check
  checkBreaches: async () => {
    const response = await api.post('/api/sla/check');
    return response.data;
  },
};

// Inventory API
export const inventoryApi = {
  // Get inventory alert settings
  getSettings: async () => {
    const response = await api.get('/api/inventory/settings');
    return response.data;
  },

  // Update inventory alert settings
  updateSettings: async (settings: {
    enabled: boolean;
    low_stock_threshold: number;
  }) => {
    const response = await api.patch('/api/inventory/settings', settings);
    return response.data;
  },

  // Get low-stock products
  getLowStockProducts: async () => {
    const response = await api.get('/api/inventory/low-stock');
    return response.data;
  },

  // Trigger manual inventory check
  checkInventory: async () => {
    const response = await api.post('/api/inventory/check');
    return response.data;
  },

  // Adjust inventory for a variant
  adjustInventory: async (variant_id: string, adjustment: number, location_id?: string) => {
    const response = await api.post('/api/inventory/adjust', {
      variant_id,
      adjustment,
      location_id,
    });
    return response.data;
  },

  // Get Shopify locations
  getLocations: async () => {
    const response = await api.get('/api/inventory/locations');
    return response.data;
  },
};

// Print Agent API
export const printAgentApi = {
  requestPairing: async (friendly_name?: string, platform?: string) => {
    const response = await api.post('/api/print/device/pair', { friendly_name, platform });
    return response.data;
  },
  listDevices: async () => {
    const response = await api.get('/api/print/devices');
    return response.data;
  },
  revokeDevice: async (deviceId: string) => {
    const response = await api.post(`/api/print/device/${deviceId}/revoke`);
    return response.data;
  },
  createJob: async (job: {
    device_id: string;
    label_data: string;
    label_format?: string;
    printer_name?: string;
    options?: any;
  }) => {
    const response = await api.post('/api/print/jobs', job);
    return response.data;
  },
  listJobs: async (params?: { status?: string }) => {
    const response = await api.get('/api/print/jobs', { params });
    return response.data;
  },
};

// Onboarding API (user registration)
export const onboardingApi = {
  // Check if current user is registered
  check: async () => {
    const response = await api.get('/api/onboarding/check');
    return response.data;
  },

  // Register a new user (creates org and user, optionally with invite token)
  register: async (organizationName?: string, inviteToken?: string) => {
    const response = await api.post('/api/onboarding/register', {
      organization_name: organizationName,
      invite_token: inviteToken,
    });
    return response.data;
  },
};

// Store Connections API (OAuth-based integrations)
export const storeConnectionsApi = {
  // List all store connections for the organization
  list: async () => {
    const response = await api.get('/api/store-connections');
    return response.data;
  },

  // Get single connection details
  get: async (id: string) => {
    const response = await api.get(`/api/store-connections/${id}`);
    return response.data;
  },

  // Initiate OAuth connection (returns redirect URL for Shopify/Faire)
  // For WooCommerce, pass { store_url, consumer_key, consumer_secret }
  // For Faire, pass { access_token, brand_name, orders_source }
  connect: async (platform: string, data?: {
    shop?: string;  // For Shopify: store domain (e.g., "mystore" or "mystore.myshopify.com")
    store_url?: string;  // For WooCommerce
    consumer_key?: string;  // For WooCommerce
    consumer_secret?: string;  // For WooCommerce
    access_token?: string;  // For Faire: personal access token
    brand_name?: string;  // For Faire: optional brand name
    orders_source?: 'shopify' | 'faire'; // For Faire: where to import orders from
  }) => {
    const response = await api.post(`/api/store-connections/connect/${platform}`, data);
    return response.data;
  },

  // Update sync settings (e.g., Faire orders source)
  updateSyncSettings: async (id: string, data: { orders_source: 'shopify' | 'faire' }) => {
    const response = await api.patch(`/api/store-connections/${id}/sync-settings`, data);
    return response.data;
  },

  // Test a store connection
  test: async (id: string) => {
    const response = await api.post(`/api/store-connections/${id}/test`);
    return response.data;
  },

  // Sync orders from a store connection
  sync: async (id: string, options?: { since?: string; limit?: number }) => {
    const response = await api.post(`/api/store-connections/${id}/sync`, options);
    return response.data;
  },

  // Reset webhooks (delete all + re-register fresh)
  // Use this when HMAC verification fails
  resetWebhooks: async (id: string) => {
    const response = await api.post(`/api/store-connections/${id}/reset-webhooks`);
    return response.data;
  },

  // Disconnect/delete a store connection
  disconnect: async (id: string) => {
    const response = await api.delete(`/api/store-connections/${id}`);
    return response.data;
  },
};

// Jobs API (background job queue)
export const jobsApi = {
  // Get job queue health status
  health: async () => {
    const response = await api.get('/api/jobs/health');
    return response.data;
  },

  // Get status of a specific job
  getStatus: async (queue: 'sync' | 'webhooks' | 'emails', jobId: string) => {
    const response = await api.get(`/api/jobs/${queue}/${jobId}`);
    return response.data;
  },

  // Queue a sync job (background processing)
  queueSync: async (type: 'products' | 'orders' | 'full' = 'orders') => {
    const response = await api.post('/api/jobs/sync', { type });
    return response.data;
  },

  // Poll job status until complete (with timeout)
  pollUntilComplete: async (
    queue: 'sync' | 'webhooks' | 'emails',
    jobId: string,
    onProgress?: (status: JobStatus) => void,
    maxWaitMs: number = 120000
  ): Promise<JobStatus> => {
    const startTime = Date.now();
    const pollInterval = 1000; // 1 second

    while (Date.now() - startTime < maxWaitMs) {
      const status = await jobsApi.getStatus(queue, jobId);
      
      if (onProgress) {
        onProgress(status);
      }

      if (status.state === 'completed' || status.state === 'failed') {
        return status;
      }

      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    throw new Error('Job timed out');
  },
};

// Job status type
export interface JobStatus {
  id: string;
  state: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed';
  progress?: number;
  result?: any;
  failedReason?: string;
  attemptsMade?: number;
  finishedOn?: number;
  processedOn?: number;
}

// Carrier types
export interface CarrierConfig {
  id: string;
  org_id: string;
  carrier_code: string;
  carrier_name: string;
  carrier_source: 'proship' | 'customer';
  shipengine_carrier_id?: string;
  enabled: boolean;
  is_default: boolean;
  priority: number;
  markup_percent: number;
  markup_flat: number;
  settings: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface ShippingMethodConfig {
  id: string;
  org_id: string;
  carrier_config_id: string;
  service_code: string;
  service_name: string;
  enabled: boolean;
  is_default: boolean;
  priority: number;
  max_weight_oz?: number;
  settings: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface CarrierWithMethods extends CarrierConfig {
  methods: ShippingMethodConfig[];
}

export interface CarrierAccount {
  id: string;
  org_id: string;
  carrier_code: string;
  account_nickname?: string;
  shipengine_carrier_id?: string;
  account_number?: string;
  connection_status: 'pending' | 'active' | 'error' | 'disconnected';
  connection_error?: string;
  connected_at?: string;
  created_at: string;
  updated_at: string;
}

// Carriers API
export const carriersApi = {
  // Get all carriers with their shipping methods
  list: async () => {
    const response = await api.get('/api/carriers');
    return response.data as {
      carriers: CarrierWithMethods[];
      available_carriers: Array<{ code: string; name: string; supportsThirdParty: boolean }>;
    };
  },

  // Get only enabled carriers
  getEnabled: async () => {
    const response = await api.get('/api/carriers/enabled');
    return response.data as {
      carriers: CarrierConfig[];
      enabled_service_codes: string[];
    };
  },

  // Sync carriers from ShipEngine
  sync: async () => {
    const response = await api.post('/api/carriers/sync');
    return response.data;
  },

  // Get available services for a carrier
  getServices: async (carrierCode: string) => {
    const response = await api.get(`/api/carriers/services/${carrierCode}`);
    return response.data;
  },

  // Toggle carrier enabled/disabled
  toggleCarrier: async (carrierId: string, enabled: boolean) => {
    const response = await api.patch(`/api/carriers/${carrierId}/toggle`, { enabled });
    return response.data;
  },

  // Set default carrier
  setDefaultCarrier: async (carrierId: string) => {
    const response = await api.post(`/api/carriers/${carrierId}/default`);
    return response.data;
  },

  // Update carrier markup
  updateMarkup: async (carrierId: string, markupPercent: number, markupFlat: number) => {
    const response = await api.patch(`/api/carriers/${carrierId}/markup`, {
      markup_percent: markupPercent,
      markup_flat: markupFlat,
    });
    return response.data;
  },

  // Toggle shipping method enabled/disabled
  toggleMethod: async (methodId: string, enabled: boolean) => {
    const response = await api.patch(`/api/carriers/methods/${methodId}/toggle`, { enabled });
    return response.data;
  },

  // Set default method for a carrier
  setDefaultMethod: async (carrierId: string, methodId: string) => {
    const response = await api.post(`/api/carriers/${carrierId}/methods/${methodId}/default`);
    return response.data;
  },

  // Get third-party carrier accounts
  getAccounts: async () => {
    const response = await api.get('/api/carriers/accounts');
    return response.data as {
      accounts: CarrierAccount[];
      supported_carriers: Array<{ code: string; name: string }>;
    };
  },

  // Connect a UPS account (simplified - ShipEngine handles OAuth)
  connectUps: async (accountInfo: {
    nickname?: string;
    account_number: string;
    postal_code: string;
    country_code: string;
    agree_to_technology_agreement: boolean;
  }) => {
    const response = await api.post('/api/carriers/accounts/ups', accountInfo);
    return response.data as {
      success: boolean;
      requires_oauth?: boolean;
      redirect_url?: string;
      account_id?: string;
      account?: any;
      error?: string;
      message?: string;
    };
  },

  // Check if UPS OAuth connection completed
  checkUpsConnection: async (accountId: string) => {
    const response = await api.get(`/api/carriers/accounts/${accountId}/check`);
    return response.data as {
      connected: boolean;
      account?: any;
    };
  },

  // Enable negotiated rates for a carrier account
  enableNegotiatedRates: async (accountId: string) => {
    const response = await api.post(`/api/carriers/accounts/${accountId}/negotiated-rates`);
    return response.data as {
      success: boolean;
      error?: string;
    };
  },

  // Disconnect a carrier account
  disconnectAccount: async (accountId: string) => {
    const response = await api.delete(`/api/carriers/accounts/${accountId}`);
    return response.data;
  },
};

// Customers API
export const customersApi = {
  // List all customers
  list: async (params?: { limit?: number; offset?: number; search?: string }) => {
    const response = await api.get('/api/customers', { params });
    return response.data;
  },

  // Get single customer
  get: async (id: string) => {
    const response = await api.get(`/api/customers/${id}`);
    return response.data;
  },

  // Get customer orders
  getOrders: async (id: string) => {
    const response = await api.get(`/api/customers/${id}/orders`);
    return response.data;
  },
};

// Blocked Addresses API - fraud / repeat-scammer prevention
export interface BlockedAddress {
  id: string;
  org_id: string;
  label?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  reason?: string | null;
  notes?: string | null;
  active: boolean;
  match_count: number;
  last_matched_at?: string | null;
  flagged_orders?: number;
  created_at: string;
  updated_at: string;
}

export interface BlockedAddressInput {
  label?: string;
  name?: string;
  email?: string;
  phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  reason?: string;
  notes?: string;
  active?: boolean;
}

export const blockedAddressesApi = {
  list: async (params?: { search?: string; active?: 'true' | 'false' }) => {
    const response = await api.get<{ blocked: BlockedAddress[]; total: number }>(
      '/api/blocked-addresses',
      { params }
    );
    return response.data;
  },

  get: async (id: string) => {
    const response = await api.get(`/api/blocked-addresses/${id}`);
    return response.data as { blocked: BlockedAddress; matches: any[] };
  },

  create: async (input: BlockedAddressInput) => {
    const response = await api.post('/api/blocked-addresses', input);
    return response.data as { blocked: BlockedAddress; retroactively_flagged: number };
  },

  update: async (id: string, input: BlockedAddressInput) => {
    const response = await api.put(`/api/blocked-addresses/${id}`, input);
    return response.data as { blocked: BlockedAddress };
  },

  remove: async (id: string) => {
    const response = await api.delete(`/api/blocked-addresses/${id}`);
    return response.data;
  },

  // Test an address against the active blocklist without creating anything
  check: async (input: BlockedAddressInput) => {
    const response = await api.post('/api/blocked-addresses/check', input);
    return response.data as {
      blocked: boolean;
      matches: Array<{
        blocked_address_id: string;
        match_type: 'address' | 'email' | 'phone' | 'name';
        matched_value: string;
        reason?: string | null;
        label?: string | null;
      }>;
    };
  },

  // Manually clear the block on an order (staff override)
  unblockOrder: async (orderId: string) => {
    const response = await api.post(`/api/blocked-addresses/orders/${orderId}/unblock`);
    return response.data;
  },
};

// Reports API
export const reportsApi = {
  // Get shipping summary report
  shippingSummary: async (params?: { start_date?: string; end_date?: string }) => {
    const response = await api.get('/api/reports/shipping-summary', { params });
    return response.data;
  },

  // Get carrier breakdown report
  carrierBreakdown: async (params?: { start_date?: string; end_date?: string }) => {
    const response = await api.get('/api/reports/carrier-breakdown', { params });
    return response.data;
  },

  // Get order volume report
  orderVolume: async (params?: { start_date?: string; end_date?: string; group_by?: 'day' | 'week' | 'month' }) => {
    const response = await api.get('/api/reports/order-volume', { params });
    return response.data;
  },
};

// Customs Settings types
export interface TaxIdentifier {
  id: string;
  type: 'vat' | 'eori' | 'ioss' | 'ein' | 'tin' | 'ssn' | 'voec' | 'oss' | 'abn';
  value: string;
  issuing_authority: string;
  entity_type: 'shipper' | 'recipient';
}

export interface CustomsSettings {
  default_contents: 'merchandise' | 'gift' | 'documents' | 'sample' | 'returned_goods' | 'other';
  default_non_delivery: 'return_to_sender' | 'treat_as_abandoned';
  default_terms_of_trade: 'ddu' | 'ddp';
  /** HS6 (or longer) tariff code applied when a product has no per-product code. */
  default_hs_tariff_code: string;
  /** ISO-2 country of origin applied when a product has none. */
  default_country_of_origin: string;
  /** Item description used when an order item has no usable title (e.g. manual orders). */
  default_item_description: string;
  declaration: string;
  tax_identifiers: TaxIdentifier[];
}

// Customs Settings API
export const customsSettingsApi = {
  // Get customs settings
  get: async (): Promise<{ customs_settings: CustomsSettings }> => {
    const response = await api.get('/api/settings/customs');
    return response.data;
  },

  // Update customs settings
  update: async (settings: Partial<Omit<CustomsSettings, 'tax_identifiers'>>) => {
    const response = await api.patch('/api/settings/customs', settings);
    return response.data;
  },

  // Add tax identifier
  addTaxIdentifier: async (identifier: Omit<TaxIdentifier, 'id'>) => {
    const response = await api.post('/api/settings/customs/tax-identifiers', identifier);
    return response.data;
  },

  // Remove tax identifier
  removeTaxIdentifier: async (id: string) => {
    const response = await api.delete(`/api/settings/customs/tax-identifiers/${id}`);
    return response.data;
  },
};

// Bundles API - for product bundles/kits
export const bundlesApi = {
  // List all bundles
  list: async () => {
    const response = await api.get('/api/bundles');
    return response.data;
  },

  // Get bundle components for a specific variant
  getComponents: async (variantId: string) => {
    const response = await api.get(`/api/bundles/variant/${variantId}`);
    return response.data;
  },

  // Create or update a bundle
  create: async (data: {
    bundle_variant_id: string;
    component_variant_id: string;
    quantity: number;
  }) => {
    const response = await api.post('/api/bundles', data);
    return response.data;
  },

  // Delete a bundle relationship
  delete: async (id: string) => {
    const response = await api.delete(`/api/bundles/${id}`);
    return response.data;
  },

  // Expand order items (replace bundles with their components for packing)
  expandForPacking: async (items: any[]) => {
    const response = await api.post('/api/bundles/expand', { items });
    return response.data;
  },
};

// Organization Settings types
export interface OrganizationSettings {
  employee_mode_enabled: boolean;
  timezone: string;
  max_package_weight_lbs?: number;
}

export interface TimezoneOption {
  value: string;
  label: string;
}

// Organization Settings API
export const organizationApi = {
  // Get organization settings
  getSettings: async (): Promise<OrganizationSettings> => {
    const response = await api.get('/api/organization/settings');
    return response.data;
  },

  // Update organization settings
  updateSettings: async (settings: { employee_mode_enabled?: boolean; timezone?: string; max_package_weight_lbs?: number }) => {
    const response = await api.patch('/api/organization/settings', settings);
    return response.data;
  },

  // Get available timezones
  getTimezones: async (): Promise<{ timezones: TimezoneOption[] }> => {
    const response = await api.get('/api/organization/timezones');
    return response.data;
  },
};

// Claims API
export const claimsApi = {
  // Get claim types reference
  getTypes: async () => {
    const response = await api.get('/api/claims/types');
    return response.data;
  },

  // List all claims
  list: async (params?: { status?: string; limit?: number; offset?: number }) => {
    const response = await api.get('/api/claims', { params });
    return response.data;
  },

  // Get claim by ID
  get: async (id: string) => {
    const response = await api.get(`/api/claims/${id}`);
    return response.data;
  },

  // Get claim by shipment ID
  getByShipment: async (shipmentId: string) => {
    const response = await api.get(`/api/claims/shipment/${shipmentId}`);
    return response.data;
  },

  // Create new claim
  create: async (data: {
    shipment_id: string;
    order_id: string;
    claim_type: string;
    description?: string;
    claim_amount?: number;
  }) => {
    const response = await api.post('/api/claims', data);
    return response.data;
  },

  // Update claim
  update: async (id: string, data: {
    status?: string;
    carrier_claim_number?: string;
    resolution_notes?: string;
    refund_amount?: number;
    note?: string;
  }) => {
    const response = await api.patch(`/api/claims/${id}`, data);
    return response.data;
  },
};

// Team / Invitation API
export const teamApi = {
  getTeam: async () => {
    const response = await api.get('/api/onboarding/team');
    return response.data;
  },

  inviteUser: async (email: string, role: string = 'member') => {
    const response = await api.post('/api/onboarding/invite', { email, role });
    return response.data;
  },

  cancelInvite: async (inviteId: string) => {
    const response = await api.delete(`/api/onboarding/invite/${inviteId}`);
    return response.data;
  },
};

export default api;
